import { useCallback, useEffect } from 'react';
import { type OpenApiSchemaDashboardTodolist, type OpenApiSchemaDashboardTodolistItem } from '#shared/api/schema';
import {
    logDashboardTodoListItemToggleComplete,
    logDashboardTodoListMarkdownLinkClick,
    logDashboardTodoListShow,
    logDashboardTodoListStepMoveBackward,
    logDashboardTodoListStepMoveForward,
} from '../lib/dashboardTodoListLogging';
import { type DashboardTodoListStepNavigationState } from '../lib/dashboardTodoListStepNavigation';

type Params = {
    isMainExpanded: boolean;
    isLoading: boolean;
    sortedItems: OpenApiSchemaDashboardTodolistItem[] | null | undefined;
    currentTodolist: OpenApiSchemaDashboardTodolist | null | undefined;
    stepNavigation: DashboardTodoListStepNavigationState | null;
    onToggleItemComplete: (item: OpenApiSchemaDashboardTodolistItem) => void;
    onMoveBackward: () => void;
    onMoveForward: () => void;
};

export function useDashboardTodoListLogging({
    isMainExpanded,
    isLoading,
    sortedItems,
    currentTodolist,
    stepNavigation,
    onToggleItemComplete,
    onMoveBackward,
    onMoveForward,
}: Params) {
    useEffect(() => {
        if (!isMainExpanded || isLoading || sortedItems == null || currentTodolist == null) {
            return;
        }

        logDashboardTodoListShow({ todolist: currentTodolist, stepNavigation });
        // eslint-disable-next-line react-hooks/exhaustive-deps -- показ этапа: раскрытие, загрузка или смена step_type, не каждое обновление пунктов
    }, [isMainExpanded, isLoading, currentTodolist?.type]);

    const onToggleItemCompleteWithLogging = useCallback(
        (item: OpenApiSchemaDashboardTodolistItem) => {
            if (currentTodolist) {
                logDashboardTodoListItemToggleComplete({
                    item,
                    stepType: currentTodolist.type,
                });
            }

            onToggleItemComplete(item);
        },
        [currentTodolist, onToggleItemComplete]
    );

    const onMoveBackwardWithLogging = useCallback(() => {
        if (currentTodolist) {
            logDashboardTodoListStepMoveBackward({ todolist: currentTodolist, stepNavigation });
        }

        onMoveBackward();
    }, [currentTodolist, onMoveBackward, stepNavigation]);

    const onMoveForwardWithLogging = useCallback(() => {
        if (currentTodolist) {
            logDashboardTodoListStepMoveForward({ todolist: currentTodolist, stepNavigation });
        }

        onMoveForward();
    }, [currentTodolist, onMoveForward, stepNavigation]);

    const onMarkdownLinkClick = useCallback(
        (params: { itemId: string; href: string }) => {
            if (!currentTodolist) {
                return;
            }

            logDashboardTodoListMarkdownLinkClick({
                href: params.href,
                itemId: params.itemId,
                stepType: currentTodolist.type,
            });
        },
        [currentTodolist]
    );

    return {
        onToggleItemCompleteWithLogging,
        onMoveBackwardWithLogging,
        onMoveForwardWithLogging,
        onMarkdownLinkClick,
    };
}
